import { useState } from 'react';
import {
  ClipboardList,
  Lock,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  AlertCircle,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useCampaignStore } from '@/stores/campaignStore';
import {
  useCampaignQuestions,
  useCreateQuestion,
  useUpdateQuestion,
  useDeactivateQuestion,
} from '@/hooks/queries/useCampaignQuestions';
import type { CampaignQuestionDto } from '@signalvote/shared';
import { CampaignQuestionType } from '@signalvote/shared';

const MAX_QUESTIONS = 5;

const TYPE_LABELS: Record<string, string> = {
  [CampaignQuestionType.YesNoUndecided]: 'Yes / No / Undecided',
  [CampaignQuestionType.MultipleChoice]: 'Multiple Choice',
  [CampaignQuestionType.FreeText]: 'Free Text',
};

// ============================================================================
// QuestionForm — shared inline form for create and edit
// ============================================================================

interface QuestionFormProps {
  mode: 'create' | 'edit';
  initial?: CampaignQuestionDto;
  displayOrder: number;
  isPending: boolean;
  error: Error | null;
  onSave: (data: { text: string; type: string; options?: string[]; displayOrder?: number }) => void;
  onCancel: () => void;
}

function QuestionForm({ mode, initial, displayOrder, isPending, error, onSave, onCancel }: QuestionFormProps) {
  const [text, setText] = useState(initial?.text ?? '');
  const [type, setType] = useState<string>(initial?.type ?? CampaignQuestionType.YesNoUndecided);
  const [options, setOptions] = useState<string[]>(initial?.options ?? ['', '']);

  const isMultipleChoice = type === CampaignQuestionType.MultipleChoice;
  const nonEmptyOptions = options.filter((o) => o.trim() !== '');
  const isValid = text.trim() !== '' && (!isMultipleChoice || nonEmptyOptions.length >= 2);
  const statusCode = (error as Error & { status?: number })?.status;

  const handleSubmit = () => {
    const data: { text: string; type: string; options?: string[]; displayOrder?: number } = {
      text: text.trim(),
      type,
    };
    if (isMultipleChoice) {
      data.options = nonEmptyOptions;
    }
    if (mode === 'create') {
      data.displayOrder = displayOrder;
    }
    onSave(data);
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <Input
        placeholder="Question text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        autoFocus
      />
      <Select value={type} onValueChange={(v) => setType(v)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={CampaignQuestionType.YesNoUndecided}>
            Yes / No / Undecided
          </SelectItem>
          <SelectItem value={CampaignQuestionType.MultipleChoice}>
            Multiple Choice
          </SelectItem>
          <SelectItem value={CampaignQuestionType.FreeText}>
            Free Text
          </SelectItem>
        </SelectContent>
      </Select>

      {isMultipleChoice && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Options (min 2)</label>
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                placeholder={`Option ${i + 1}`}
                value={opt}
                onChange={(e) => {
                  const next = [...options];
                  next[i] = e.target.value;
                  setOptions(next);
                }}
              />
              {options.length > 2 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => setOptions(options.filter((_, j) => j !== i))}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOptions([...options, ''])}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Option
          </Button>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-1.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {statusCode === 409 ? error.message : 'Something went wrong. Please try again.'}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSubmit} disabled={!isValid || isPending}>
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
              Saving...
            </>
          ) : mode === 'create' ? (
            'Add Question'
          ) : (
            'Save'
          )}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// QuestionRow — single question in the list
// ============================================================================

interface QuestionRowProps {
  question: CampaignQuestionDto;
  onEdit: () => void;
  onDeactivate: () => void;
  isDeactivating: boolean;
}

function QuestionRow({ question, onEdit, onDeactivate, isDeactivating }: QuestionRowProps) {
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border p-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{question.text}</p>
        <div className="mt-1 flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {TYPE_LABELS[question.type] ?? question.type}
          </Badge>
          {question.type === CampaignQuestionType.MultipleChoice && question.options && (
            <span className="text-xs text-muted-foreground">
              {question.options.length} options
            </span>
          )}
          {question.hasAnswers && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" />
              Has answers
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {!question.hasAnswers && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}

        {confirmingDeactivate ? (
          <div className="flex items-center gap-1 text-sm">
            <span className="text-muted-foreground">Deactivate?</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-destructive"
              onClick={() => {
                onDeactivate();
                setConfirmingDeactivate(false);
              }}
              disabled={isDeactivating}
            >
              {isDeactivating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7"
              onClick={() => setConfirmingDeactivate(false)}
            >
              No
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => setConfirmingDeactivate(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CampaignQuestionsCard — main export
// ============================================================================

export function CampaignQuestionsCard() {
  const canManageFieldOps = useCampaignStore((s) => s.canManage('FieldOps'));
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);

  const { data: questions, isLoading, isError } = useCampaignQuestions();

  const createMutation = useCreateQuestion();
  const updateMutation = useUpdateQuestion();
  const deactivateMutation = useDeactivateQuestion();

  // Destructure mutate functions (golden rule — reference-stable)
  const { mutate: createQuestion, reset: resetCreate } = createMutation;
  const { mutate: updateQuestion, reset: resetUpdate } = updateMutation;
  const { mutate: deactivateQuestion } = deactivateMutation;

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (!selectedCampaign || !canManageFieldOps) return null;

  const activeQuestions = questions ?? [];
  const count = activeQuestions.length;

  const openCreate = () => {
    setEditingId(null);
    resetCreate();
    setShowCreateForm(true);
  };

  const openEdit = (id: string) => {
    setShowCreateForm(false);
    resetUpdate();
    setEditingId(id);
  };

  const closeAll = () => {
    setShowCreateForm(false);
    setEditingId(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-5 w-5" />
          Survey Questions
          <Badge variant="outline" className="ml-auto text-xs font-normal">
            {count}/{MAX_QUESTIONS}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-md border p-3 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-1.5 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Failed to load questions. Please refresh the page.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && count === 0 && !showCreateForm && (
          <div className="flex flex-col items-center py-6 text-center">
            <ClipboardList className="h-10 w-10 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              No survey questions yet. Add questions for volunteers to ask at the door.
            </p>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" />
              Add Question
            </Button>
          </div>
        )}

        {/* Question list */}
        {!isLoading && !isError && activeQuestions.map((q) =>
          editingId === q.id ? (
            <QuestionForm
              key={q.id}
              mode="edit"
              initial={q}
              displayOrder={q.displayOrder}
              isPending={updateMutation.isPending}
              error={updateMutation.error}
              onSave={(data) => {
                updateQuestion(
                  { questionId: q.id, updates: data },
                  { onSuccess: () => setEditingId(null) }
                );
              }}
              onCancel={closeAll}
            />
          ) : (
            <QuestionRow
              key={q.id}
              question={q}
              onEdit={() => openEdit(q.id)}
              onDeactivate={() => deactivateQuestion(q.id)}
              isDeactivating={deactivateMutation.isPending}
            />
          )
        )}

        {/* Inline create form */}
        {showCreateForm && (
          <QuestionForm
            mode="create"
            displayOrder={count + 1}
            isPending={createMutation.isPending}
            error={createMutation.error}
            onSave={(data) => {
              createQuestion(data, { onSuccess: closeAll });
            }}
            onCancel={closeAll}
          />
        )}

        {/* Add button (when list has items, form is closed, under limit) */}
        {!isLoading && !isError && count > 0 && !showCreateForm && editingId === null && count < MAX_QUESTIONS && (
          <Button variant="outline" size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" />
            Add Question
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
